import {
  clientSeesLabel,
  resolveBuyingPower,
  TRUSTED_BID_LIMIT_MAX,
  TRUSTED_BID_LIMIT_MIN,
  type BiddingModeKind,
  type BiddingModePut,
  type BuyingPower,
  type ClientSeesLabel,
  type DepositMultiplier,
} from '@carsfinders/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { fetchBiddingJournal, fetchBiddingMode, updateBiddingMode } from '../api';
import { type BiddingModeSnapshot } from '../types';

/** What the operator is editing on the card — the three values `users.bidding` stores. */
export interface BiddingDraft {
  mode: BiddingModeKind;
  multiplier: DepositMultiplier;
  bidLimit: number;
}

/**
 * The starting draft for a legacy client (no `users.bidding` yet): owner
 * decision (e), 2026-09-27 — deposit ×10 (was ×4 before the ×5/×10/×15 switch) for deposit clients, 10 bids for
 * trusted ones. Nothing is stored until the operator presses Save.
 */
export const LEGACY_DRAFT: BiddingDraft = {
  mode: 'deposit',
  multiplier: 10,
  bidLimit: 10,
};

/** The saved mode as a draft, or `null` for a legacy client. */
export function savedDraftOf(snapshot: BiddingModeSnapshot): BiddingDraft | null {
  const bidding = snapshot.bidding;
  if (!bidding) return null;
  return {
    mode: bidding.mode,
    multiplier: bidding.multiplier,
    bidLimit: bidding.bidLimit,
  };
}

/**
 * Dirty = the draft differs from what is stored in the one parameter the mode
 * uses. A legacy client is always dirty: its first Save is what sets a mode.
 */
export function isDraftDirty(saved: BiddingDraft | null, draft: BiddingDraft): boolean {
  if (!saved) return true;
  if (saved.mode !== draft.mode) return true;
  return draft.mode === 'deposit'
    ? saved.multiplier !== draft.multiplier
    : saved.bidLimit !== draft.bidLimit;
}

export interface BiddingPreview {
  buyingPower: BuyingPower;
  clientSees: ClientSeesLabel;
}

/**
 * The numbers the card shows for `draft`. When the draft is the saved mode the
 * API's own answer is used verbatim; an unsaved choice is resolved with
 * `resolveBuyingPower` / `clientSeesLabel` from @carsfinders/contracts — the
 * SAME pure functions the API enforces with, so the preview cannot disagree
 * with enforcement. No formula lives in the admin app.
 */
export function previewOf(snapshot: BiddingModeSnapshot, draft: BiddingDraft): BiddingPreview {
  const saved = savedDraftOf(snapshot);
  if (saved && !isDraftDirty(saved, draft) && snapshot.buyingPower && snapshot.clientSees) {
    return {
      buyingPower: snapshot.buyingPower,
      clientSees: snapshot.clientSees,
    };
  }
  const buyingPower = resolveBuyingPower(
    draft,
    snapshot.depositChf,
    snapshot.committedChf,
    snapshot.activeBidCount,
  );
  return { buyingPower, clientSees: clientSeesLabel(buyingPower) };
}

/**
 * The warning rule 02 asks for BEFORE saving: the new ceiling (or bid count)
 * sits below what the client already has live. Existing bids stay; only new
 * ones are blocked. Shown for an unsaved choice only — the saved state has its
 * own «Перевищено…» note.
 */
export function belowCommittedWarning(
  preview: BiddingPreview,
  dirty: boolean,
): 'ceiling' | 'count' | null {
  if (!dirty) return null;
  const bp = preview.buyingPower;
  if (bp.mode === 'deposit') return bp.overCeiling ? 'ceiling' : null;
  return bp.bidLimit < bp.activeBidCount ? 'count' : null;
}

/**
 * A stored `bidding.bidLimit` is only `int ≥ 0` (it may be a mirror of the
 * legacy `users.bidLimit`, e.g. 0 or 120), while a trusted PUT accepts 1..50.
 * Switching to trusted starts from the nearest valid value instead.
 */
export function clampBidLimit(value: number): number {
  return Math.min(TRUSTED_BID_LIMIT_MAX, Math.max(TRUSTED_BID_LIMIT_MIN, Math.round(value)));
}

/** A draft `BiddingModePutSchema` would accept — Save stays off otherwise. */
export function isDraftValid(draft: BiddingDraft): boolean {
  return draft.mode === 'deposit' || clampBidLimit(draft.bidLimit) === draft.bidLimit;
}

/** The PUT body: only the parameter the mode accepts (`BiddingModePutSchema` is strict). */
export function putBodyOf(draft: BiddingDraft): BiddingModePut {
  return draft.mode === 'deposit'
    ? { mode: 'deposit', multiplier: draft.multiplier }
    : { mode: 'trusted', bidLimit: draft.bidLimit };
}

export const biddingModeKey = (userId: string) => ['user', userId, 'bidding-mode'] as const;
export const biddingJournalKey = (userId: string) =>
  ['user', userId, 'bidding-mode', 'journal'] as const;

/** Snapshot + journal reads and the Save mutation of the «Режим ставок» card. */
export function useBiddingMode(userId: string) {
  const queryClient = useQueryClient();

  const snapshotQuery = useQuery({
    queryKey: biddingModeKey(userId),
    queryFn: () => fetchBiddingMode(userId),
  });
  const journalQuery = useQuery({
    queryKey: biddingJournalKey(userId),
    queryFn: () => fetchBiddingJournal(userId),
  });

  const saveMutation = useMutation({
    mutationFn: (draft: BiddingDraft) => updateBiddingMode(userId, putBodyOf(draft)),
    onSuccess: async () => {
      // The prefix also refreshes the journal; `clients` carries the list's limit caption.
      await queryClient.invalidateQueries({ queryKey: biddingModeKey(userId) });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
    },
  });

  return { snapshotQuery, journalQuery, saveMutation };
}
